import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { switchMap, takeUntil } from 'rxjs/operators';
import { SnapShotService } from '../../../services/snap-shot.service';

import { ModalShareEmailComponent } from '../modal-share-email/modal-share-email.component';
import { ModalAiSnapshotAuthComponent } from '../modal-ai-snapshot-auth/modal-ai-snapshot-auth.component';
import { AuthService } from '../../../services/auth.service';
import { ProjectManagerService } from '../../../services/project-manager.service';
import { ReplicateAiImageService } from '../../../services/replicate-ai-image.service';
import { AiSettingsDialogService } from '../../../services/ai-settings-dialog.service';
import { ToasterService } from 'angular2-toaster';

interface SnapshotImage {
  isActive: boolean;
  url: string | null;
}

@Component({
  selector: 'app-modal-view-snapshot',
  templateUrl: './modal-view-snapshot.component.html',
  styleUrls: ['./modal-view-snapshot.component.scss']
})
export class ModalViewSnapshotComponent implements OnInit, OnDestroy {
  images: SnapshotImage[] = [];
  currentImg: SnapshotImage = { isActive: false, url: null };
  activeImageIndex: number = 0;
  isAiGenerating = false;
  isSavedProject = false;
  isImagePreviewOpen = false;
  isAuthorization = false;
  isSiteAdmin = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ModalViewSnapshotComponent>,
    public snapshotSrvc: SnapShotService,
    public dialog: MatDialog,
    public projectSrvc: ProjectManagerService,
    private auth: AuthService,
    private replicateAiImageService: ReplicateAiImageService,
    private aiSettingsDialogService: AiSettingsDialogService,
    private toasterService: ToasterService
  ) {}

  switchImg(value: number) {
    if (this.activeImageIndex + value >= 0 && this.activeImageIndex + value < this.images.length) {
      this.activeImageIndex += value;
      this.setCurrentImg(this.activeImageIndex);
    }
  }

  setCurrentImg(value: number) {
    this.activeImageIndex = value;
    this.currentImg = this.images[this.activeImageIndex];
    this.images.forEach((img, i) => (img.isActive = i === value));
  }

  shareThroughEmail() {
    let selectedImg = new Array();
    for (let i = 0; i < this.images.length; i++) {
      if (this.images[i].isActive == true) {
        selectedImg.push(this.images[i].url);
      }
    }
    this.dialogRef.close();
    let dialogRef2 = this.dialog.open(ModalShareEmailComponent, {
      width: '500px',
      data: selectedImg
    });

    dialogRef2.afterClosed().pipe(takeUntil(this.destroy$)).subscribe();
  }

  deleteSnapshot() {
    let deletedImg = this.images.splice(this.activeImageIndex, 1);
    //update snapshots in service
    let masImg = new Array();
    for (let i = 0; i < this.images.length; i++) {
      masImg[i] = this.images[i].url;
    }
    this.snapshotSrvc.snapshots = masImg;

    const deletedUrl = deletedImg[0].url;
    if (deletedUrl && this.isServerSnapshotUrl(deletedUrl)) {
      this.snapshotSrvc
        .deleteSnapShot(deletedUrl)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => console.log(res));
    }

    if (this.isSavedProject) {
      this.persistSnapshots(masImg);
    }

    if (this.images.length != this.activeImageIndex) {
      this.currentImg = this.images[this.activeImageIndex];
      this.currentImg.isActive = true;
    } else {
      if (this.images.length) {
        this.activeImageIndex--;
        this.currentImg = this.images[this.activeImageIndex];
      } else {
        this.currentImg = { isActive: false, url: null };
      }
    }
  }

  openAiSettings(): void {
    this.aiSettingsDialogService.open();
  }

  generateAiSnapshot() {
    if (!this.currentImg.url || this.isAiGenerating) {
      return;
    }

    if (!this.auth.isAuthenticated()) {
      this.dialog.open(ModalAiSnapshotAuthComponent, {
        width: '560px',
        panelClass: 'ai-snapshot-auth-dialog'
      });
      return;
    }

    const selectedImageUrl = this.currentImg.url;
    this.isAiGenerating = true;

    this.replicateAiImageService
      .generateRealisticImage(selectedImageUrl)
      .pipe(
        switchMap(generatedUrl => this.snapshotSrvc.compressImageForSave(generatedUrl)),
        takeUntil(this.destroy$)
      )
      .subscribe(
        compressedUrl => {
          const newImage: SnapshotImage = { isActive: true, url: compressedUrl };
          this.images.forEach(img => (img.isActive = false));
          this.images.push(newImage);
          this.activeImageIndex = this.images.length - 1;
          this.currentImg = newImage;

          const masImg = this.images.map(img => img.url);
          this.persistSnapshots(masImg);

          this.finishAiGeneration('success', 'Success', 'AI image generated');
        },
        err => {
          const message =
            (err && err.message) || (typeof err === 'string' ? err : 'AI image generation failed');
          this.finishAiGeneration('error', 'Error', message);
        }
      );
  }

  private finishAiGeneration(type: string, title: string, body: string) {
    this.isAiGenerating = false;
    this.toasterService.popAsync(type, title, body);
  }

  private persistSnapshots(masImg: string[]) {
    this.snapshotSrvc.setSnapshots(masImg);

    if (!this.isSavedProject) {
      return;
    }

    const projectId = this.projectSrvc.getCurrentProjectId();
    this.snapshotSrvc
      .persistSnapshotsForProject(projectId, masImg)
      .pipe(takeUntil(this.destroy$))
      .subscribe(saved => {
        this.snapshotSrvc.setSnapshots(saved);
      });
  }

  private isServerSnapshotUrl(url: string): boolean {
    return !!url && !this.snapshotSrvc.isDataUrl(url);
  }

  openImagePreview() {
    if (this.currentImg.url) {
      this.isImagePreviewOpen = true;
    }
  }

  closeImagePreview() {
    this.isImagePreviewOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeImagePreview();
  }

  printSnapshot() {
    const win = window.open();
    const doc = win.document;
    const body = doc.body;
    const img = <any>doc.createElement('img');
    img.src = this.currentImg.url;
    body.style.margin = '0';
    body.style.padding = '0';
    img.decode()
      .then(() => {
        body.appendChild(img);

        win.print();
      })

    // printJS({
    //   printable: this.currentImg.url,
    //   type: 'image',
    //   documentTitle: 'Snapshot'
    // });
  }

  ngOnInit() {
    this.isAuthorization = this.auth.isAuthorization.value;
    this.isSiteAdmin = this.isAuthorization && this.auth.getRole() === 'admin';

    this.auth.isAuthorization.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthorization = auth;
      this.isSiteAdmin = auth && this.auth.getRole() === 'admin';
    });

    this.projectSrvc.isSavedProject.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.isSavedProject = value;
    });

    let masImg = this.snapshotSrvc.getSnapshots();
    if (masImg.length > 0) {
      for (let i = 0; i < masImg.length; i++) {
        let element: SnapshotImage = {
          isActive: false,
          url: masImg[i]
        };
        this.images.push(element);
      }
      this.currentImg = this.images[this.activeImageIndex];
      this.currentImg.isActive = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/modals/modal-view-snapshot/modal-view-snapshot.component.ts